import {
  acceptanceConfirmationLabel,
  engagementStatusLabel,
  AcceptanceConfirmation,
  UPDATE_INTAKE_LOCK,
} from '@kanos/domain';
import { Button, CheckboxGroupField, TextField } from '@kanos/ui';
import {
  formatDate,
  formatDateTime,
  meQuery,
  ProblemAlert,
  QueryContent,
  startSignIn,
  stepUpLasts,
  useIdempotencyKey,
} from '@kanos/web-core';
import { useQuery } from '@tanstack/react-query';
import { FileText, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { useBranding } from './branding';
import {
  engagementQuery,
  useAccept,
  useDecline,
  useOpenDocument,
  type OfferedContract,
  type RevocationReceipt,
} from './engagement-queries';
import { RunningContractSection } from './engagement-contract';
import { ContractSection as Section, Receipt, Withdrawal } from './engagement-withdrawal';
import { intakesQuery } from './intake-queries';
import { Lead, PageLoading, PortalPage } from './portal-page';

const HERE = '/engagement';

/** The address of this page for the intake - where the mails lead and the step-up returns. */
const hereFor = (caseId: string) => `${HERE}?case=${encodeURIComponent(caseId)}`;

/**
 * „Ihr Steuerberatungsvertrag“ (ADR-072 §§ 4-5): the firm's offer with its documents, accepted
 * with every confirmation and a fresh second factor - locked while the client is not identified -
 * or declined; a consumer's withdrawal function while the period runs.
 */
export function EngagementPage() {
  const branding = useBranding();
  const me = useQuery(meQuery);
  const signedIn = me.data?.app === 'client';
  const intakes = useQuery({ ...intakesQuery, enabled: signedIn });
  const asked = new URLSearchParams(window.location.search).get('case');

  const content = () => {
    if (me.isPending) return <PageLoading />;
    if (!signedIn) {
      return (
        <div className="grid gap-6">
          <Lead title="Ihr Steuerberatungsvertrag">
            Bitte melden Sie sich mit dem Konto an, mit dem Sie die Einladung Ihrer Kanzlei
            angenommen haben.
          </Lead>
          <Button
            className="justify-self-start"
            onPress={() => startSignIn(asked ? hereFor(asked) : HERE)}
          >
            Anmelden
          </Button>
        </div>
      );
    }
    return (
      <QueryContent query={intakes}>
        {(list) => {
          const intake = list.find((i) => i.id === asked) ?? list[0];
          if (!intake) {
            return (
              <Lead title="Ihr Steuerberatungsvertrag">
                Zu Ihrem Konto gibt es bei {branding?.firmName ?? 'dieser Kanzlei'} noch kein
                Angebot.
              </Lead>
            );
          }
          return (
            <Contract caseId={intake.id} stepUpValidUntil={me.data?.stepUpValidUntil ?? null} />
          );
        }}
      </QueryContent>
    );
  };

  return <PortalPage branding={branding}>{content()}</PortalPage>;
}

function Contract({
  caseId,
  stepUpValidUntil,
}: {
  caseId: string;
  stepUpValidUntil: string | null;
}) {
  const engagement = useQuery(engagementQuery(caseId));
  // Kept here: once withdrawn, the withdrawal function goes - the receipt stays in sight.
  const [receipt, setReceipt] = useState<RevocationReceipt | null>(null);
  return (
    <QueryContent query={engagement}>
      {({ offer, contract }) =>
        offer ? (
          <div className="grid gap-6">
            <Lead title="Ihr Steuerberatungsvertrag">
              {contract && contract.version < offer.version
                ? `Ein Nachtrag von ${offer.firmName} zu Ihrem Vertrag, Fassung ${String(offer.version)} vom ${formatDateTime(offer.issuedAt)}.`
                : `Das Angebot von ${offer.firmName} für ${offer.clientName}, Fassung ${String(offer.version)} vom ${formatDateTime(offer.issuedAt)}.`}
            </Lead>
            {contract && <RunningContractSection caseId={caseId} contract={contract} />}
            {/* The contract's own offer: its documents are the contract's, shown above. */}
            {!(contract && contract.version >= offer.version) && <Standing offer={offer} />}
            {!(contract && contract.version >= offer.version) && (
              <Documents caseId={caseId} offer={offer} />
            )}
            {offer.open && offer.locks.length > 0 && (
              <Locked keys={offer.locks.map((l) => l.key)} />
            )}
            {offer.open && offer.locks.length === 0 && (
              <Acceptance caseId={caseId} offer={offer} stepUpValidUntil={stepUpValidUntil} />
            )}
            {offer.open && <Decline caseId={caseId} version={offer.version} />}
            {receipt && <Receipt receipt={receipt} firmName={offer.firmName} />}
            {offer.revocable && !receipt && (
              <Withdrawal caseId={caseId} offer={offer} onDone={setReceipt} />
            )}
          </div>
        ) : (
          <Lead title="Ihr Steuerberatungsvertrag">
            Ihre Kanzlei hat Ihnen noch kein Angebot gestellt. Sobald es da ist, erhalten Sie eine
            E-Mail.
          </Lead>
        )
      }
    </QueryContent>
  );
}

/** Where the offer stands - in words, never by colour alone. */
function Standing({ offer }: { offer: OfferedContract }) {
  const line = (() => {
    if (offer.revokedAt)
      return `Sie haben den Vertrag am ${formatDateTime(offer.revokedAt)} widerrufen.`;
    if (offer.acceptedAt)
      return `Sie haben das Angebot am ${formatDateTime(offer.acceptedAt)} angenommen.`;
    if (offer.declinedAt)
      return `Sie haben das Angebot am ${formatDateTime(offer.declinedAt)} abgelehnt.`;
    if (offer.open) return `Das Angebot gilt bis zum ${formatDate(offer.offerValidUntil)}.`;
    return `Das Angebot gilt nicht mehr (${engagementStatusLabel[offer.status]}). Bitte wenden Sie sich an Ihre Kanzlei.`;
  })();
  return (
    <p className="glass rounded-lg p-4 text-sm" role="status">
      {line}
      {offer.withdrawalUntil && offer.revocable
        ? ` Sie können den Vertrag bis zum ${formatDate(offer.withdrawalUntil)} widerrufen.`
        : ''}
    </p>
  );
}

function Documents({ caseId, offer }: { caseId: string; offer: OfferedContract }) {
  const open = useOpenDocument(caseId);
  return (
    <Section title="Ihre Vertragsunterlagen">
      <p className="text-sm text-muted">
        Bitte lesen Sie alle Unterlagen und speichern Sie sie als PDF. Sie bleiben unverändert hier
        in Ihrem Portal.
      </p>
      <ul className="grid gap-2">
        {offer.documents.map((d) => (
          <li key={d.id} className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-sm">{d.title}</span>
            <Button
              variant="secondary"
              size="sm"
              isPending={open.isPending && open.variables.document === d.id}
              onPress={() => open.mutate({ document: d.id, version: offer.version })}
            >
              <FileText aria-hidden className="size-4" />
              PDF öffnen
            </Button>
          </li>
        ))}
      </ul>
      {open.isError && <ProblemAlert error={open.error} />}
    </Section>
  );
}

/**
 * Not acceptable yet: while the firm brings the earlier intake up to date after the mandate's end
 * (decided 2026-11-03), a calm word that nothing is the client's to do; else not identified yet
 * (§ 11 Abs. 1 GwG), the way there instead of the button.
 */
function Locked({ keys }: { keys: readonly string[] }) {
  if (keys.every((key) => key === UPDATE_INTAKE_LOCK)) {
    return (
      <Section title="Annahme noch nicht möglich">
        <p className="text-sm">
          Ihre Kanzlei aktualisiert gerade die Angaben aus Ihrer früheren Mandatsaufnahme. Sobald
          sie damit fertig ist, können Sie das Angebot hier annehmen – Sie müssen dafür nichts tun.
        </p>
      </Section>
    );
  }
  return (
    <Section title="Annahme noch nicht möglich">
      <p className="text-sm">
        Annehmen können Sie das Angebot, sobald Ihre Kanzlei Sie als Vertragspartei identifiziert
        hat. Bitte schließen Sie zuerst Ihre Angaben und die Identifizierung ab – den Link zur
        Online-Identifizierung schickt Ihnen Ihre Kanzlei per E-Mail.
      </p>
      <Button
        variant="secondary"
        className="justify-self-start"
        onPress={() => window.location.assign('/intake')}
      >
        Zu Ihren Angaben
      </Button>
    </Section>
  );
}

/**
 * The confirmations ticked before the second factor, kept in this tab while the identity provider
 * asks for it - back in the portal they are ticked again, once. Nothing personal: only which of
 * the fixed confirmations were given.
 */
function keepTicked(key: string, ticked: readonly AcceptanceConfirmation[]) {
  try {
    sessionStorage.setItem(key, JSON.stringify(ticked));
  } catch {
    // Storage blocked: the person ticks them again after the second factor.
  }
}

function takeTicked(key: string): AcceptanceConfirmation[] {
  try {
    const raw = sessionStorage.getItem(key);
    sessionStorage.removeItem(key);
    const parsed: unknown = raw === null ? [] : JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter(
          (c): c is AcceptanceConfirmation => AcceptanceConfirmation.safeParse(c).success,
        )
      : [];
  } catch {
    // Storage blocked or the kept value unreadable: nothing ticked yet.
    return [];
  }
}

function Acceptance({
  caseId,
  offer,
  stepUpValidUntil,
}: {
  caseId: string;
  offer: OfferedContract;
  stepUpValidUntil: string | null;
}) {
  const accept = useAccept(caseId);
  const [idempotencyKey] = useIdempotencyKey();
  const kept = `kanos.acceptance.${caseId}.${String(offer.version)}`;
  const [ticked] = useState(() => takeTicked(kept));
  const { required, optional } = offer.confirmations;
  const [given, setGiven] = useState<AcceptanceConfirmation[]>(() =>
    ticked.filter((c) => required.includes(c)),
  );
  const [wished, setWished] = useState<AcceptanceConfirmation[]>(() =>
    ticked.filter((c) => optional.includes(c)),
  );
  const complete = required.every((c) => given.includes(c));
  const fresh = stepUpLasts(stepUpValidUntil);
  const options = (list: readonly AcceptanceConfirmation[]) =>
    list.map((id) => ({ id, label: acceptanceConfirmationLabel[id] }));

  return (
    <Section title="Angebot annehmen">
      <CheckboxGroupField
        label="Ihre Bestätigungen"
        description="Bitte bestätigen Sie jeden Punkt einzeln."
        options={options(required)}
        value={given}
        onChange={setGiven}
      />
      {optional.length > 0 && (
        <CheckboxGroupField
          label="Nur auf Ihren ausdrücklichen Wunsch"
          description="Ohne diese Angaben beginnt die Kanzlei erst nach Ende der Widerrufsfrist."
          options={options(optional)}
          value={wished}
          onChange={setWished}
        />
      )}
      {/* § 312j Abs. 2 BGB: the essentials right above the button. */}
      <div className="grid gap-2 rounded-sm bg-fill p-4 text-sm ring-1 ring-hairline ring-inset">
        <h3 className="font-semibold">Das Wichtigste zu Ihrer Bestellung</h3>
        <p>
          Vertragspartner: {offer.firmName}. Vertrag: Steuerberatungsvertrag, Fassung{' '}
          {String(offer.version)}
          {offer.serviceStart ? `, Leistungsbeginn ${formatDate(offer.serviceStart)}` : ''}.
        </p>
        <ul className="grid gap-1">
          {offer.services.map((s) => (
            <li key={s.title} className="flex flex-wrap justify-between gap-2">
              <span>{s.title}</span>
              <span className="text-muted">{s.price}</span>
            </li>
          ))}
        </ul>
        <p className="text-muted">
          Laufzeit, Kündigung und alle weiteren Bedingungen stehen im Steuerberatungsvertrag und in
          der Vergütungsvereinbarung oben.
        </p>
      </div>
      {accept.isError && <ProblemAlert error={accept.error} />}
      {fresh ? (
        <Button
          className="justify-self-start"
          isDisabled={!complete}
          isPending={accept.isPending}
          onPress={() =>
            accept.mutate({
              body: { version: offer.version, confirmations: [...given, ...wished] },
              idempotencyKey,
            })
          }
        >
          {offer.consumer ? 'zahlungspflichtig bestellen' : 'Vertrag annehmen'}
        </Button>
      ) : (
        <div className="grid gap-2">
          <p className="text-sm text-muted">
            Zum Annehmen bestätigen Sie sich zuerst noch einmal mit Ihrem zweiten Faktor.
          </p>
          <Button
            variant="secondary"
            className="justify-self-start"
            onPress={() => {
              keepTicked(kept, [...given, ...wished]);
              startSignIn(hereFor(caseId), 'step-up');
            }}
          >
            <ShieldCheck aria-hidden className="size-4" />
            Mit zweitem Faktor bestätigen
          </Button>
        </div>
      )}
    </Section>
  );
}

function Decline({ caseId, version }: { caseId: string; version: number }) {
  const decline = useDecline(caseId);
  const [idempotencyKey] = useIdempotencyKey();
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState('');
  if (!asking) {
    return (
      <Button variant="quiet" className="justify-self-start" onPress={() => setAsking(true)}>
        Angebot ablehnen
      </Button>
    );
  }
  return (
    <Section title="Angebot ablehnen">
      <TextField
        label="Grund (freiwillig)"
        placeholder="z. B. Wir bleiben bei unserer bisherigen Kanzlei."
        multiline
        rows={3}
        value={reason}
        onChange={setReason}
      />
      {decline.isError && <ProblemAlert error={decline.error} />}
      <div className="flex flex-wrap gap-2">
        <Button variant="quiet" onPress={() => setAsking(false)}>
          Abbrechen
        </Button>
        <Button
          variant="danger"
          isPending={decline.isPending}
          onPress={() =>
            decline.mutate({
              body: { version, reason: reason.trim() ? reason.trim() : null },
              idempotencyKey,
            })
          }
        >
          Ablehnung senden
        </Button>
      </div>
    </Section>
  );
}
